import { apiCall, apiCallWithCsrfRefresh } from "./api";
import type {
  BulkCreateItem,
  BulkResponse,
  CreatePlannedWorkoutInput,
  CreateResponse,
  ListResponse,
  PlannedWorkout,
  UpdatePlannedWorkoutInput,
  UpdateResponse
} from "./plannerTypes";

const BASE = "/api/planned-workouts";

/** Mirror `bulkBodySchema` caps in `src/modules/planner/schema.ts`. */
const MAX_BULK_DELETE_IDS = 128;
const MAX_BULK_CREATE_ITEMS = 128;

function chunk<T>(items: T[], size: number): T[][] {
  if (items.length <= size) return [items];
  const batches: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    batches.push(items.slice(i, i + size));
  }
  return batches;
}

export async function listWorkouts(range: { from: string; to: string }, signal?: AbortSignal): Promise<ListResponse> {
  return apiCall<ListResponse>(BASE, { query: range, signal });
}

export async function createWorkout(
  input: CreatePlannedWorkoutInput,
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<CreateResponse> {
  return apiCallWithCsrfRefresh<CreateResponse>(
    BASE,
    { method: "POST", body: input, csrfToken },
    refreshCsrf
  );
}

export async function patchWorkout(
  id: string,
  patch: UpdatePlannedWorkoutInput,
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<PlannedWorkout> {
  const res = await apiCallWithCsrfRefresh<UpdateResponse>(
    `${BASE}/${encodeURIComponent(id)}`,
    { method: "PATCH", body: patch, csrfToken },
    refreshCsrf
  );
  return res.workout;
}

export async function deleteWorkout(
  id: string,
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<void> {
  await apiCallWithCsrfRefresh<void>(
    `${BASE}/${encodeURIComponent(id)}`,
    { method: "DELETE", csrfToken },
    refreshCsrf
  );
}

export async function deleteSeries(
  repeatGroupId: string,
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<number> {
  const res = await apiCallWithCsrfRefresh<{ deleted: number }>(
    `${BASE}/series/${encodeURIComponent(repeatGroupId)}`,
    { method: "DELETE", csrfToken },
    refreshCsrf
  );
  return res.deleted;
}

/** Low-level `/bulk` call — any combination of create/createMany/update/delete. */
async function bulk(
  body: {
    create?: { template: Omit<BulkCreateItem, "date">; dates: string[] };
    createMany?: { items: BulkCreateItem[]; planGroupId?: string };
    update?: { ids: string[]; patch: UpdatePlannedWorkoutInput };
    delete?: { ids: string[] };
  },
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<BulkResponse> {
  return apiCallWithCsrfRefresh<BulkResponse>(
    `${BASE}/bulk`,
    { method: "POST", body, csrfToken },
    refreshCsrf
  );
}

/**
 * Create many *distinct* workouts in one transaction — copy/paste of a
 * selection or week, and Apply-a-plan. Returns the created rows (for undo).
 * Pass `planGroupId` to tag an applied plan so it can be removed as a set.
 */
export async function bulkCreateDistinct(
  items: BulkCreateItem[],
  csrfToken: string,
  refreshCsrf: () => Promise<string>,
  planGroupId?: string
): Promise<PlannedWorkout[]> {
  if (items.length === 0) return [];
  const created: PlannedWorkout[] = [];
  for (const batch of chunk(items, MAX_BULK_CREATE_ITEMS)) {
    const res = await bulk(
      { createMany: planGroupId ? { items: batch, planGroupId } : { items: batch } },
      csrfToken,
      refreshCsrf
    );
    created.push(...res.created);
  }
  return created;
}

/** Apply one patch to many ids in a single transaction. */
export async function bulkUpdate(
  ids: string[],
  patch: UpdatePlannedWorkoutInput,
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<PlannedWorkout[]> {
  const res = await bulk({ update: { ids, patch } }, csrfToken, refreshCsrf);
  return res.updated;
}

/** Delete many rows by id (paste-undo). Returns the count deleted. */
export async function bulkDelete(
  ids: string[],
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<number> {
  if (ids.length === 0) return 0;
  let deleted = 0;
  for (const batch of chunk(ids, MAX_BULK_DELETE_IDS)) {
    const res = await bulk({ delete: { ids: batch } }, csrfToken, refreshCsrf);
    deleted += res.deleted;
  }
  return deleted;
}

/**
 * Atomic "Replace day": clear `deleteIds`, then create `items`, in ONE
 * transaction so a day is never left cleared with the paste failed.
 */
export async function bulkReplace(
  deleteIds: string[],
  items: BulkCreateItem[],
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<BulkResponse> {
  return bulk({ delete: { ids: deleteIds }, createMany: { items } }, csrfToken, refreshCsrf);
}

export async function notifyConnectorInterest(
  provider: string,
  email: string | undefined,
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<void> {
  await apiCallWithCsrfRefresh<{ recorded: boolean }>(
    `/api/connectors/${encodeURIComponent(provider)}/interest`,
    { method: "POST", body: email ? { email } : {}, csrfToken },
    refreshCsrf
  );
}
